/**
 * Shared print helper for the Report 2 (A4) canvas. Consumed by BOTH:
 *   1. The Print button inside <ReportV2Panel /> (top of the A4 canvas).
 *   2. The top-toolbar Print button in <TestProceduresModule /> — when
 *      the Report 2 tab is active it short-circuits to this helper so
 *      the operator gets exactly the same output no matter which button
 *      they hit. No divergent codepaths, no divergent output.
 *
 * Never touches the existing Report tab's flow — the App.css
 * `@media print` rule is scoped to `body.printing-report-v2`.
 */
export const printReportV2 = () => {
  const CLASS = 'printing-report-v2';
  document.body.classList.add(CLASS);
  // rAF so the browser has a chance to apply the print CSS before the
  // native print dialog opens; otherwise Chrome sometimes rasterises
  // the pre-class layout on the first paint.
  requestAnimationFrame(() => {
    window.print();
    const cleanup = () => document.body.classList.remove(CLASS);
    window.addEventListener('afterprint', cleanup, { once: true });
    // Belt-and-suspenders — some browsers never fire afterprint.
    setTimeout(cleanup, 4000);
  });
};

// A4 canvas constants — the on-screen preview is measured in CSS
// pixels at 96 dpi (`794 × 1123 = 210 × 297 mm`) so the audiologist
// sees the report at full paper size while editing. Print output is
// scoped separately via `App.css @media print` to `190 × 277 mm`
// (the browser Print dialog's default 10 mm margin region) so the
// paper never gets browser-scaled to fit — see the `body.printing-
// report-v2 #report-v2-canvas` block for the print-time geometry.
export const A4_WIDTH_PX = 794;   // 210 mm @ 96 dpi — SCREEN preview
export const A4_HEIGHT_PX = 1123; // 297 mm @ 96 dpi — SCREEN preview
export const A4_PADDING_PX = 38;  // ≈ 10 mm — matches the previous 10 mm padding.
export const A4_CONTENT_WIDTH_PX  = A4_WIDTH_PX  - A4_PADDING_PX * 2; // 718
export const A4_CONTENT_HEIGHT_PX = A4_HEIGHT_PX - A4_PADDING_PX * 2; // 1047
